'use client';

import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Button } from '~/components/Buttons';
import { Container } from '~/components/Container';
import type { AcfLink } from '~/types/graphql';

type AcfHighlights = {
  highlights: {
    cta?: AcfLink;
    fieldGroupName?: string;
    headline?: string;
    highlightPlaylistTags?: string;
    image?: {
      node: {
        sourceUrl: string;
        altText?: string;
      };
    };
    subhead?: string;
  } | null;
  scoreplayTags?: string | null;
};

export function Highlights({
  scoreplayTags,
  highlights,
  className,
}: AcfHighlights & { className?: string }) {
  if (!highlights) return null;

  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const {
    headline,
    subhead,
    cta,
    image,
    //  highlightPlaylistTags
  } = highlights;

  if (!scoreplayTags) return null;

  const fetchVideos = async () => {
    try {
      const response = await fetch(
        `/api/scoreplay?tags=${encodeURIComponent(scoreplayTags)}&type=player`,
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error);
      }

      const videoData = data.data.media
        .filter((item: any) => item.type === 'video')
        .sort(
          (a: any, b: any) =>
            new Date(b.CreatedAt).getTime() - new Date(a.CreatedAt).getTime(),
        )
        .slice(0, 3);

      setVideos(videoData);
    } catch (err: any) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVideos();
  }, []);

  const hasVideos = false;
  // const hasVideos = videos.length > 0;
  const hasArticle = highlights;

  if (!hasVideos && !hasArticle) return null;

  return (
    <div
      className={clsx(
        'flex flex-col gap-y-16 p-[--site-margin] py-16',
        className,
      )}
    >
      {(hasArticle || hasVideos) && (
        <Container className="mb-[-64px] flex justify-between">
          <div className="mb-6 flex items-center justify-between lg:mb-8">
            <h3 className="text-[32px] font-black uppercase leading-none lg:text-[36px]">
              Highlights
            </h3>
          </div>
        </Container>
      )}
      {hasArticle && (
        <Container className="gap-8 lg:flex-row lg:gap-16">
          <div className="flex w-full flex-col gap-y-9 lg:max-w-[640px] lg:gap-y-6 lg:py-9">
            <h2 className="w-fit font-nova font-black uppercase leading-none lg:relative lg:text-[36px]">
              {headline}
            </h2>
            {subhead && (
              <div
                className="mb-0 max-w-[528px] font-nova text-[16px] font-normal leading-[24px] lg:text-xl lg:leading-[30px]"
                dangerouslySetInnerHTML={{ __html: subhead }}
              />
            )}
            {!!cta?.url && (
              <Button isLarge href={cta.url}>
                {cta.title}
              </Button>
            )}
          </div>

          <div className="flex max-lg:order-first">
            {image?.node?.sourceUrl && (
              <img
                className="aspect-[608/345] w-full rounded object-cover"
                src={image.node.sourceUrl}
                alt={image?.node?.altText ?? ''}
              />
            )}
          </div>
        </Container>
      )}
      {loading ? (
        <Container>
          <div className="flex items-center justify-center py-12">
            <p>Loading highlights...</p>
          </div>
        </Container>
      ) : hasVideos ? (
        <Container>
          <Swiper
            className="w-full !pb-12 max-lg:mb-9"
            wrapperClass="!h-auto"
            slidesPerView={1}
            spaceBetween={20}
            modules={[Pagination]}
            pagination={{ clickable: true }}
            breakpoints={{
              768: {
                slidesPerView: 'auto',
                spaceBetween: 32,
              },
              992: {
                spaceBetween: 32,
                slidesPerView: 3,
              },
            }}
          >
            {videos.map((video: any, i: any) => (
              <SwiperSlide
                className="flex max-w-[416px] flex-col items-start justify-start gap-6 gap-y-6"
                key={video.ID}
              >
                <div className="w-full">
                  <video
                    className="w-full rounded"
                    controls
                    poster={video.thumbnail_url}
                    preload="none"
                  >
                    <source src={video.proxy_medium} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                  {video?.description && (
                    <h3 className="mb-6 mt-[24px] max-w-[90%] font-nova text-[20px] font-semibold normal-case leading-[30px] lg:mt-[32px]">
                      {video.description}
                    </h3>
                  )}
                  <div className="mt-6 flex gap-x-6 font-roboto text-[12px] font-semibold uppercase leading-[16px] tracking-[2px]">
                    <span>
                      {new Date(video.CreatedAt).toLocaleDateString()}
                    </span>
                    <span>Highlight</span>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          <Button className="lg:hidden" href="#">
            Watch More
          </Button>
        </Container>
      ) : error ? (
        <Container>
          <div className="flex items-center justify-center py-12">
            <p>Error loading highlights</p>
          </div>
        </Container>
      ) : null}
    </div>
  );
}
